<script lang="ts" setup>
import { Popup } from "vant";
import { computed } from "vue";
import { MAX_CHAR_PER_LINE } from "@/components/mobile/gal-game/editor/utils";
import { useGalGameEditor } from "@/stores/galGameEditor";

const props = defineProps<{
  show: boolean;
}>();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
}>();
const store = useGalGameEditor();
const lines = computed(() => store.modalInputContent!.split("\n"));
const maxCharPerLine = computed(
  () => (3 - lines.value.length) * MAX_CHAR_PER_LINE
);
const isLineCountOverLimit = computed(() => lines.value.length > 2);
const isCharCountOverLimit = computed(() =>
  lines.value.some((e) => e.length > maxCharPerLine.value)
);
const isOverLimit = computed(
  () => isLineCountOverLimit.value || isCharCountOverLimit.value
);

const onConfirm = () => {
  store.activeDialog.text = store.modalInputContent;
  store.saveDialog();
  emit("update:show", false);
};
</script>
<template>
  <popup
    :close-on-click-overlay="false"
    :show="show"
    class="min-h-[30%] overflow-hidden grid grid-rows-[auto_1fr]"
    position="bottom"
    round
    @update:show="emit('update:show', $event)"
  >
    <threads-nav :go-back="() => emit('update:show', false)" show-back>
      <template #center>{{ $t("game.pleaseInputDialog") }}</template>
      <template #right>
        <button class="btn btn-xs btn-primary md:btn-sm" @click="onConfirm">
          {{ $t("Buttons.confirm") }}
        </button>
      </template>
    </threads-nav>
    <div class="flex flex-col m-4 gap-2 text-base-content/50">
      <div class="flex gap-1 items-center">
        <i-shape-icon />
        {{ $t("game.dialogLimitTip") }}
      </div>
      <VanField
        v-model="store.modalInputContent"
        :class="{
          'border-error': isOverLimit,
          'border-secondary': !isOverLimit,
        }"
        :placeholder="$t('game.pleaseInputDialog')"
        :row="2"
        autosize
        class="border"
        type="textarea"
      />
      <div class="flex items-start gap-2 text-base-content/50 justify-between">
        <div :class="{ 'text-error': isLineCountOverLimit }">
          {{ $t("game.lineLimit") }}: {{ lines.length }}/2
        </div>
        <div class="flex gap-1 justify-end">
          <div :class="{ 'text-error': isCharCountOverLimit }">
            {{ $t("game.charLimit") }}
          </div>
          <div class="flex flex-col items-end">
            <div
              v-for="(line, idx) in lines.slice(0, 2)"
              :key="idx"
              :class="{ 'text-error': line.length > maxCharPerLine }"
              class="flex gap-1 justify-between"
            >
              <span>{{ $t("game.row") }}{{ idx + 1 }}:</span>
              <span>{{ line.length }} / {{ maxCharPerLine }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </popup>
</template>
